'use client'

import Image from 'next/image'
import React, { useState } from 'react'

import { Image as ImageIcon, Plus, UploadIcon, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import {
  FieldWrapper,
  FieldWrapperClasses
} from '@/components/form/field-wrapper'
import { useFieldContext } from '@/components/form/form'

import { cn, isFieldInvalid } from '@/utils'

type ImageComponentProps = {
  accept?: string
  alt?: string
  classNames?: FieldWrapperClasses
  disabled?: boolean
  height?: number
  hideRemoveButton?: boolean
  label?: string
  name?: string
  placeholder?: string
  required?: boolean
  src: string | undefined
  width?: number
}

export const ImageComponent: React.FC<ImageComponentProps> = ({
  accept = 'image/*',
  alt,
  classNames = {},
  disabled = false,
  height = 150,
  hideRemoveButton = true,
  label,
  name,
  placeholder,
  required,
  src,
  width = 150
}) => {
  const t = useTranslations('components.form.imageComponent')
  const field = useFieldContext<File | null | undefined>()
  const value = field.state.value
  const [dragOver, setDragOver] = useState(false)
  const defaultAlt = alt ?? t('alt')
  const defaultPlaceholder = placeholder ?? t('placeholder')

  // Get image source - handle both File objects and string URLs
  const getImageSrc = (): null | string => {
    if (!value && !src) return null
    if (value instanceof File) return URL.createObjectURL(value)
    if (src) return src
    return null
  }

  const imageSrc = getImageSrc()
  const hasImage = !!imageSrc
  const isInvalid = isFieldInvalid(field)

  // Handle file selection
  const handleFileSelect = (file: File | null) => {
    if (file && !disabled) {
      field.handleChange(file)
      field.handleBlur?.()
    }
  }

  // Open file picker
  const openFilePicker = () => {
    if (disabled) return

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.name = name || 'image'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0] || null
      handleFileSelect(file)
    }
    input.click()
  }

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)

    if (disabled) return

    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file)
    }
  }

  // Handle remove
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled) {
      field.handleChange(null)
      field.handleBlur?.()
    }
  }

  // Handle edit (replace)
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled) {
      openFilePicker()
    }
  }
  return (
    <FieldWrapper
      classNames={{
        childrenWrapper: cn(
          `max-h-[${height}px] max-w-[${width}px]!`,
          classNames.childrenWrapper
        ),
        label: classNames.label,
        wrapper: classNames.wrapper
      }}
      field={field}
      label={label}
      required={required}
    >
      {/* Main Container */}
      <div
        data-invalid={isInvalid || undefined}
        onBlur={field.handleBlur}
        onClick={!hasImage ? openFilePicker : () => {}}
        onDragLeave={!disabled ? handleDragLeave : () => {}}
        onDragOver={!disabled ? handleDragOver : () => {}}
        onDrop={!disabled ? handleDrop : () => {}}
        onKeyDown={(e) => !disabled && e.key === 'Enter' && openFilePicker()}
        role="button"
        style={{ height, width }}
        tabIndex={0}
      >
        {hasImage ? (
          <>
            {/* Display Image */}
            <Image
              alt={defaultAlt}
              className={cn(
                'h-full w-full object-cover transition-opacity duration-200',
                disabled && 'opacity-50',
                !disabled && isInvalid && 'opacity-75',
                !disabled && !isInvalid && 'opacity-100'
              )}
              height={height}
              src={imageSrc}
              unoptimized={value instanceof File} // Don't optimize File objects
              width={width}
            />

            {/* Action Buttons Overlay */}
            {!disabled && (
              <div className="absolute start-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 gap-5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 rtl:translate-x-1/2">
                <button
                  aria-label={t('editImage')}
                  className="bg-background/90 text-foreground border-border hover:bg-background flex size-10 cursor-pointer items-center justify-center rounded-full border shadow-lg hover:scale-105"
                  onClick={handleEdit}
                  type="button"
                >
                  <UploadIcon size={16} />
                </button>
                {!hideRemoveButton && (
                  <button
                    aria-label={t('removeImage')}
                    className="bg-destructive/80 hover:bg-destructive text-destructive-foreground focus:ring-ring flex size-10 cursor-pointer items-center justify-center rounded-full p-1.5 shadow-lg transition-all duration-160 hover:scale-110 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                    onClick={handleRemove}
                    type="button"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {/* Hover overlay */}
            <div className="bg-background/0 group-hover:bg-background/50 absolute inset-0 transition-all duration-200" />
          </>
        ) : (
          /* Add Image Placeholder */
          <div
            className={cn(
              'flex h-full flex-col items-center justify-center transition-colors duration-200',
              dragOver
                ? 'text-primary'
                : 'text-muted-foreground group-hover:text-foreground'
            )}
          >
            <div
              className={cn(
                'mb-2 rounded-full p-3 transition-colors duration-200',
                dragOver ? 'bg-primary/20' : 'bg-muted group-hover:bg-muted/80'
              )}
            >
              {dragOver ? <ImageIcon size={24} /> : <Plus size={24} />}
            </div>
            <p className="px-2 text-center text-sm font-medium">
              {dragOver ? t('dropHere') : defaultPlaceholder}
            </p>
            <p className="text-muted-foreground/60 mt-1 text-center text-xs whitespace-break-spaces">
              {dragOver ? t('releaseToUpload') : t('clickOrDrag')}
            </p>
          </div>
        )}
      </div>
    </FieldWrapper>
  )
}
